import './DataGrid.css'

import {
  AllCommunityModule,
  CellClickedEvent,
  ColDef,
  CsvExportModule,
  GridReadyEvent,
  GridStateModule,
  ModuleRegistry,
  QuickFilterModule,
  RowClickedEvent,
  SelectionChangedEvent,
  themeAlpine,
} from 'ag-grid-community'
import { AgGridReact, AgGridReactProps } from 'ag-grid-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Spinner } from 'react-bootstrap'

import { DownloadIcon } from '../icons/DownloadIcon'
import ColumnOrganizer from './ColumnOrganizer'

ModuleRegistry.registerModules([AllCommunityModule, CsvExportModule, QuickFilterModule, GridStateModule])

const myTheme = themeAlpine.withParams({})
interface DataGridProps extends AgGridReactProps {
  enableSorting?: boolean
  enableFiltering?: boolean
  enablePagination?: boolean
  hideSearch?: boolean
  searchPlaceholder?: string
  entityName?: string
  showRowNumbers?: boolean
  hideHeader?: boolean
  hideColumnOrganizer?: boolean
  columnDefs?: ColDef[]
}
export const DataGrid = ({
  rowData = [],
  columnDefs = [],
  onSelectionChanged,
  onCellClicked,
  onRowClicked,
  onGridReady,
  rowSelection,
  enableSorting = true,
  enableFiltering = true,
  enablePagination = false,
  paginationPageSize = 10,
  className = '',
  defaultColDef = {},
  animateRows = true,
  domLayout = 'autoHeight',
  hideSearch = false,
  searchPlaceholder = 'Search...',
  entityName = 'default',
  showRowNumbers,
  hideHeader = false,
  hideColumnOrganizer = false,
  ...props
}: DataGridProps) => {
  const gridRef = useRef<AgGridReact>(null)
  const [searchText, setSearchText] = useState('')
  const [columnOrder, setColumnOrder] = useState<string[]>([])

  // Initialize column order from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(`${entityName}ColumnsState`)
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as string[]
        if (parsed && Array.isArray(parsed)) {
          const validFields = new Set(columnDefs.map(c => c.field))
          const filteredColumns = parsed.filter(col => validFields.has(col))
          setColumnOrder(filteredColumns)
        }
      } catch {}
    } else {
      setColumnOrder(columnDefs.filter(col => col.colId !== 'actions' && col.hide !== true).map(col => col.field as string))
    }
  }, [columnDefs, entityName])

  const onSetColumnsState = useCallback(
    (columns: string[]) => {
      setColumnOrder(columns)
      localStorage.setItem(`${entityName}ColumnsState`, JSON.stringify(columns))
    },
    [entityName]
  )

  // Handle search text changes
  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value)
  }, [])

  // Handle export
  const handleExport = useCallback(() => {
    if (gridRef.current) {
      const gridApi = gridRef.current.api
      gridApi.exportDataAsCsv({
        fileName: `${entityName}_export.csv`,
        allColumns: true,
      })
    }
  }, [entityName])

  // Default column definitions
  const defaultColDefs = useMemo(
    () => ({
      sortable: enableSorting,
      filter: enableFiltering,
      resizable: true,
      minWidth: 100,
      flex: 1,
      ...defaultColDef,
    }),
    [enableSorting, enableFiltering, defaultColDef]
  )

  // Prepare columns for ColumnOrganizer
  const columnOrganizerColumns = useMemo(() => {
    return columnDefs
      .filter(col => col.colId !== 'actions')
      .map(col => ({
        field: col.field || col.colId,
        headerName: col.headerName || col.field,
      }))
  }, [columnDefs])

  // Order and filter columns based on columnOrder
  const visibleColumnDefs = useMemo(() => {
    if (columnOrder.length === 0) {
      return columnDefs
    }

    // Only include columns in columnOrder, in that order, and not hidden
    const hiddenColumns = columnDefs
      .filter(c => !columnOrder.includes(c.field as string))
      .map(c => ({
        ...c,
        hide: true,
      }))

    const colMap = new Map(columnDefs.map(col => [col.field, col]))
    const actionsCol = columnDefs.find(col => col.colId === 'actions')
    const res: ColDef[] = columnOrder
      .filter(field => colMap.has(field))
      .map(field => ({
        ...colMap.get(field),
        hide: false,
      }))

    // Add row number column if enabled
    if (showRowNumbers) {
      res.unshift({
        colId: 'rowNumber',
        headerName: '#',
        filter: false,
        sortable: false,
        minWidth: 60,
        maxWidth: 80,
        width: 60,
        hide: false,
        cellRenderer: (params: { node: { rowIndex: number } }) => {
          return params.node.rowIndex + 1
        },
        cellStyle: { textAlign: 'center', fontWeight: 'bold' },
      })
    }

    if (actionsCol) {
      res.push({
        ...actionsCol,
        hide: false,
      })
    }
    return [...res, ...hiddenColumns]
  }, [columnDefs, columnOrder, showRowNumbers])

  // Handle row selection change
  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent) => {
      if (onSelectionChanged) {
        onSelectionChanged(event)
      }
    },
    [onSelectionChanged]
  )

  // Handle cell click
  const handleCellClicked = useCallback(
    (event: CellClickedEvent) => {
      if (onCellClicked) {
        onCellClicked(event)
      }
    },
    [onCellClicked]
  )

  // Handle row click
  const handleRowClicked = useCallback(
    (event: RowClickedEvent) => {
      if (onRowClicked) {
        onRowClicked(event)
      }
    },
    [onRowClicked]
  )

  // Handle grid ready
  const handleGridReady = useCallback(
    (event: GridReadyEvent) => {
      if (onGridReady) {
        onGridReady(event)
      }
    },
    [onGridReady]
  )

  // Grid options
  const gridOptions = useMemo(
    () => ({
      rowData,
      columnDefs: visibleColumnDefs,
      defaultColDef: defaultColDefs,
      rowSelection,
      animateRows,
      domLayout,
      pagination: enablePagination,
      paginationPageSize,
      quickFilterText: searchText,
      ...props,
    }),
    [
      rowData,
      visibleColumnDefs,
      defaultColDefs,
      rowSelection,
      animateRows,
      domLayout,
      enablePagination,
      paginationPageSize,
      searchText,
      props,
    ]
  )

  return (
    <div className={`data-grid-container ${className}`}>
      {/* Toolbar with search, export, and column organizer */}
      {!hideHeader && (
        <div className='data-grid-toolbar'>
          {!hideSearch && (
            <input
              type='text'
              placeholder={searchPlaceholder}
              value={searchText}
              onChange={e => handleSearchChange(e.target.value)}
              className='data-grid-search'
            />
          )}
          <Button title='Export' size='sm' onClick={handleExport} className='btn-icon'>
            <DownloadIcon />
          </Button>
          {!hideColumnOrganizer && (
            <ColumnOrganizer columns={columnOrganizerColumns} columnFieldsToShow={columnOrder} onChange={onSetColumnsState} />
          )}
        </div>
      )}

      {/* Grid */}
      <div className={`ag-theme-alpine ${className}`} style={{ height: 'calc(100% - 60px)', overflowY: 'auto' }}>
        <AgGridReact
          ref={gridRef}
          theme={myTheme}
          {...gridOptions}
          loadingOverlayComponent={() => <Spinner animation='border' role='status' size='sm' />}
          onSelectionChanged={handleSelectionChanged}
          onCellClicked={handleCellClicked}
          onRowClicked={handleRowClicked}
          onGridReady={handleGridReady}
        />
      </div>
    </div>
  )
}

export default DataGrid
